import { useEffect, useRef } from 'react';
import { isIOS, isAndroid, isNative } from '../services/platform';

/**
 * Runs once on app start. Configures the iOS/Android status bar, hides the
 * Capacitor splash screen, and wires up the Android hardware back button
 * + OAuth deep-link bridge. All Capacitor imports are dynamic so the hook
 * is a no-op on web.
 *
 * Pattern is taken verbatim from
 * `shared-docs/capacitor-native-setup.md` § "Native Setup Hook".
 */
export default function useNativeSetup() {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        // iOS/Android: Set status bar style
        if (isIOS() || isAndroid()) {
          const { StatusBar, Style } = await import('@capacitor/status-bar');
          await StatusBar.setStyle({ style: Style.Light });
          if (isAndroid()) {
            await StatusBar.setBackgroundColor({ color: '#0a0a1a' });
          }
        }

        // Hide splash screen
        if (isNative()) {
          const { SplashScreen } = await import('@capacitor/splash-screen');
          await SplashScreen.hide();
        }

        // Android hardware back button
        if (isAndroid()) {
          const { App } = await import('@capacitor/app');
          App.addListener('backButton', ({ canGoBack }) => {
            if (canGoBack) {
              window.history.back();
            } else {
              App.exitApp();
            }
          });
        }

        // OAuth deep-link handler — closes the in-app browser when the
        // /auth/callback URL is delivered from the system browser.
        if (isNative()) {
          const { App } = await import('@capacitor/app');
          App.addListener('appUrlOpen', async ({ url }) => {
            if (url?.includes('/auth/callback')) {
              const { Browser } = await import('@capacitor/browser');
              await Browser.close();
            }
          });
        }
      } catch (err) {
        console.warn('Native setup error (non-fatal):', err);
      }
    })();
  }, []);
}
